import * as React from 'react';
import { connect } from 'react-redux';
import HTML5Backend from 'react-dnd-html5-backend';
import { DragDropContext } from 'react-dnd';

import ProjectHOC from '../getProjectHOC';
import NotifyHOC from '../../../HOC/Notifications';
import QuestionBuilder from './QuestionBuilder/QuestionBuilder';
import QuestionTypesModal from './questionType/questionTypesModal';

import QuestionListView from './QuestionListView';
import { IApplicationState } from '../../../reducers';
import { QTYPES, QuestionID, QuestionSummary } from '@nexxt/common/types';
import {
    setAddNewQuestion,
    editQuestion,
    setLogicQuestion,
    clearQuestionStatus
} from '../../../actions/question.actions';
import {
    getSortedQuestionByProjectId,
    sortQuestions,
    saveUpdatedSortedQuestion,
    getQuestionByQuestionId,
    deleteQuestion,
    editGreeting,
    previewQuestion,
    duplicateQuestion
} from '../../../services/question.service';
import { getTrainedModelSummaries } from '../../../services/conversational.service';
import { BUILDER_STATES } from '../../../interface/common.interface';
import { getConfirmationBox } from '../../common/swal/Sweetalert';
import { clearLocationState, splitPathname } from '../../../utils/string';
import { Project, SPECIAL_QIDS } from '@nexxt/common/types';
import DuplicateQuestionModal from './common/duplicateModal/DuplicateQuestionModal';
import {
    parseAlertMessage,
    alertMessage
} from '../../../constants/alertMessage';
import { sortHealthProblemByQuestionID } from '../../../utils/project';
import { PROJECT_EDIT_MODES } from '../../../interface/project.interface';
import {
    getValueFromLocalStorage,
    setValueToLocalStorage
} from '@nexxt/common/utils/storage';
import { flattenQuestionSummary } from '../../../utils/question';

export interface IProjectQuestionsState {
    questionSearch: string;
    isLoading: boolean;
    isPreviewReady: boolean;
    isQuestionLoading: boolean;
    questionTypeModal: boolean;
    isDuplicateQuestion: boolean;
    duplicatedQuestion: any;
    isAudience: boolean;
    addQuestion: any;
    initialSelectedQuestionID: SPECIAL_QIDS;
}

export interface IProjectQuestionsProps {
    toastNotification: any;
    editGreeting: (type: QTYPES) => void;
    setAddNewQuestion: (
        questionType: string,
        position: number,
        parent: number
    ) => void;
    projectId: number;
    currentQuestion: any;
    // isLoading: boolean;
    currentQuestionId: number;
    questionSummaries: QuestionSummary[];
    builderState: any; // @TODO: typing
    isAdd: boolean;
    isEdited: boolean;
    editMode: PROJECT_EDIT_MODES;
    editedType: string;
    editEnabled: boolean;
    currentProject: Project;
    projectHealth: any;
    location: any;
    history: any;
    viewMode: string;
    currentLockUser: any;
    formRenderCount: number;
    showError: boolean;
    getTrainedModelSummaries: () => Promise<void>;
    getSortedQuestionByProjectId: (projectId: number) => Promise<any>;
    clearQuestionStatus: ({ currentQuestion, builderState }) => Promise<any>;
    getQuestionByQuestionId: (
        questionId: QuestionID,
        options?: any
    ) => Promise<any>;
    setLogicQuestion: ({ question, questionId, builderState }) => Promise<any>;
    deleteQuestion: (question: any, isParent: boolean) => Promise<any>;
    duplicateQuestion: (question: any) => Promise<any>;
    sortQuestions: (
        projectId: number,
        dragIndex: number,
        hoverIndex: number,
        questionId: number
    ) => Promise<any>;
    saveUpdatedSortedQuestion: (projectId: number) => Promise<any>;
    previewQuestion: (
        projectId: number,
        questionId: QuestionID
    ) => Promise<any>;
}

class Questions extends React.Component<
    IProjectQuestionsProps,
    IProjectQuestionsState
> {
    constructor(props) {
        super(props);
        this.state = {
            questionSearch: '',
            isLoading: true,
            isPreviewReady: false,
            isQuestionLoading: false,
            questionTypeModal: false,
            isDuplicateQuestion: false,
            duplicatedQuestion: {},
            isAudience: false,
            addQuestion: { position: 0, parent: 0 },
            initialSelectedQuestionID: SPECIAL_QIDS.GREETING_TEXT
        };
    }
    async componentDidMount() {
        // const query = getQueryString(QUERY_TYPE.MODAL_OPEN);
        const locationState = this.props.location.state;
        if (locationState?.enableAudience) {
            this.setState({ questionTypeModal: true, isAudience: true });
        }

        // Fetch trained model summaries to state
        await this.props.getTrainedModelSummaries();

        // Fetch Questions
        const { projectId, questionSummaries } = this.props;
        if (!(questionSummaries || []).length)
            await this.props.getSortedQuestionByProjectId(projectId);

        if (this.props.location?.state?.enableAudience) {
            return;
        }
        const initialSelectedQuestionID =
            this.getInitialSelectedQuestionIdFromStorage();
        if (initialSelectedQuestionID < 0) {
            this.props.clearQuestionStatus({
                builderState: BUILDER_STATES.EDIT,
                currentQuestion: {
                    type:
                        initialSelectedQuestionID === SPECIAL_QIDS.GREETING_TEXT
                            ? QTYPES.GREETING_TEXT
                            : QTYPES.REDIRECT
                }
            });
        } else {
            await this.props.getQuestionByQuestionId(
                initialSelectedQuestionID,
                {
                    edit: true
                }
            );
        }
        this.setState({
            isLoading: false
        });
    }

    componentWillUnmount() {
        this.props.clearQuestionStatus({
            builderState: BUILDER_STATES.EDIT,
            currentQuestion: {}
        });
    }
    /**
     * Checks local storage for projectBuilderSessionMap.projectId to set initialSelectedQuestion
     * If this object doesnt exist or if the object of this projectID doesnt exist it will create one
     * and set selectedQuestion to SPECIAL_QIDS.GREETING_TEXT
     * SPECIAL_QIDS.GREETING_TEXT = Greeting Text. SPECIAL_QIDS.REDIRECT_TEXT = Redirect Text
     * @returns number
     */
    getInitialSelectedQuestionIdFromStorage = (): number => {
        let initialSelectedQuestionID = SPECIAL_QIDS.GREETING_TEXT;
        const projectID = this.props.projectId;
        const projectBuilderSessionMap = getValueFromLocalStorage(
            'projectBuilderSessionMap'
        );

        if (!projectBuilderSessionMap) {
            const updatedSession = {};
            updatedSession[projectID] = {
                selectedQuestion: SPECIAL_QIDS.GREETING_TEXT
            };
            setValueToLocalStorage('projectBuilderSessionMap', updatedSession);
        } else if (!projectBuilderSessionMap[projectID]) {
            projectBuilderSessionMap[projectID] = {
                selectedQuestion: SPECIAL_QIDS.GREETING_TEXT
            };
            setValueToLocalStorage(
                'projectBuilderSessionMap',
                projectBuilderSessionMap
            );
        } else {
            // Check to make sure id still exists in questionSummaries
            const check = flattenQuestionSummary(
                this.props.questionSummaries
            ).some(
                (q) =>
                    q.id ===
                    projectBuilderSessionMap[projectID].selectedQuestion
            );
            if (!check) {
                this.saveSelectedQuestionToStorage(SPECIAL_QIDS.GREETING_TEXT);
            } else {
                initialSelectedQuestionID =
                    projectBuilderSessionMap[projectID].selectedQuestion;
            }
            this.setState({
                initialSelectedQuestionID: initialSelectedQuestionID
            });
        }
        return initialSelectedQuestionID;
    };

    /**
     * Saves the selected question to local storage. Similar to checkStorage above
     * @param questionID
     */
    saveSelectedQuestionToStorage = (questionID) => {
        const projectID = this.props.projectId;
        const projectBuilderSessionMap = getValueFromLocalStorage(
            'projectBuilderSessionMap'
        );

        if (!projectBuilderSessionMap) {
            const updatedSession = {};
            updatedSession[projectID] = {
                selectedQuestion: questionID
            };
            setValueToLocalStorage('projectBuilderSessionMap', updatedSession);
        } else {
            projectBuilderSessionMap[this.props.projectId] = {
                selectedQuestion: questionID
            };
            setValueToLocalStorage(
                'projectBuilderSessionMap',
                projectBuilderSessionMap
            );
        }
    };
    onSave = () => {};

    onCancel = () => {};

    onQuestionSave = (questionSummary: QuestionSummary) => {};

    onQuestionSearch = (searchText: string) => {
        this.setState({
            questionSearch: searchText
        });
    };

    isEdited = () => {
        return (
            (this.props.isAdd ||
                this.props.builderState === BUILDER_STATES.EDIT) &&
            this.props.isEdited
        );
    };

    // @TODO: the isEdited() check should be moved elsewhere
    // (e.g. a wrapper could check for isEdited() and _then_ call this func)
    onQuestionEdit = async (
        questionSummary: QuestionSummary,
        forceParent = false
    ) => {
        if (
            [QTYPES.GREETING_TEXT, QTYPES.REDIRECT].includes(
                questionSummary.type as QTYPES
            )
        ) {
            // Greeting question = SPECIAL_QIDS.GREETING_TEXT, Redirect question = -SPECIAL_QIDS.REDIRECT_TEXT
            const questionId =
                questionSummary.type === QTYPES.GREETING_TEXT
                    ? SPECIAL_QIDS.GREETING_TEXT
                    : SPECIAL_QIDS.REDIRECT_TEXT;
            this.saveSelectedQuestionToStorage(questionId);
            this.props.editGreeting(questionSummary.type as QTYPES);
            return;
        }
        this.setState(
            {
                isQuestionLoading: true
            },
            () => {
                let questionId = questionSummary.id;
                if (forceParent) {
                    questionId = questionSummary.parent;
                }
                this.saveSelectedQuestionToStorage(questionId);
                this.props
                    .getQuestionByQuestionId(questionId, { edit: true })
                    .then(() => {
                        this.setState({
                            isQuestionLoading: false
                        });
                    });
            }
        );
    };

    onQuestionLogic = async (questionSummary: QuestionSummary) => {
        this.setState(
            {
                isQuestionLoading: true
            },
            async () => {
                await this.props.getQuestionByQuestionId(questionSummary.id, {
                    logic: true
                });
            }
        );
        this.setState({
            isQuestionLoading: false
        });
    };

    onQuestionDelete = async (questionSummary: QuestionSummary) => {
        const result = await getConfirmationBox({
            message: questionSummary.childrenIds.length
                ? alertMessage.QUESTION_DELETE_GROUP
                : questionSummary.isAudience
                ? alertMessage.CONFIRM_DELETE_DEMOGRAPHIC_QUESTION
                : alertMessage.CONFIRM_CONTINUE,
            showCloseButton: true,
            confirmButtonColor: '',
            title: alertMessage.DELETE_ITEM
        });
        if (result.value) {
            this.setState({ isLoading: true });
            try {
                const isParent = [QTYPES.GROUP, QTYPES.POPUP].includes(
                    questionSummary.type as QTYPES
                );

                await this.props.deleteQuestion(questionSummary, isParent);
                this.props.toastNotification.success(
                    parseAlertMessage(
                        alertMessage.QUESTION_DELETE_SUCCESS,
                        questionSummary.label
                    )
                );
                this.setState({ isLoading: false });
            } catch (e) {
                this.setState({ isLoading: false });

                this.props.toastNotification.error(
                    alertMessage.UNABLE_QUESTION_DELETE
                );
            }
        } else {
            this.setState({ isLoading: false });
        }
    };

    onQuestionPreview = async (questionSummary: QuestionSummary) => {
        this.setState({ isPreviewReady: false }, async () => {
            if (questionSummary.type === QTYPES.GREETING_TEXT) {
                await this.props.previewQuestion(this.props.projectId, -1);
            } else {
                await this.props.getQuestionByQuestionId(questionSummary.id); // set q as active
                await this.props.previewQuestion(
                    this.props.projectId,
                    questionSummary.id
                );
            }
            this.setState({ isPreviewReady: true });
        });
    };

    onQuestionSelect = async (
        questionSummary: QuestionSummary,
        forceParent = false
    ) => {
        // confirm continue if user is currently editing question
        let result: any = {};
        if (this.isEdited()) {
            result = await getConfirmationBox({
                message: alertMessage.CONFIRM_CONTINUE,
                showCloseButton: true,
                confirmButtonColor: '',
                title: alertMessage.UNDO_CURRENT_CHANGE
            });
            if (!result.value) {
                return;
            }
        }
        if (
            this.props.currentQuestion?.id &&
            questionSummary.id === this.props.currentQuestion?.id
        ) {
            return;
            // activate question
        }
        await this.onQuestionEdit(questionSummary, forceParent);

        // @TODO: fix bug where directly switching from one PREVIEW
        // state to another spawns multiple bot sessions

        // if (this.props.builderState === BUILDER_STATES.PREVIEW) {
        //     this.onQuestionPreview(question);
        // } else {
        //     // otherwise just trigger selected question
        //     await this.onQuestionEdit(question);
        // }
    };

    onNewQuestionAdd = async (position = -1, parent = 0) => {
        // TODO: Open popup which would allow to select from question type
        try {
            let result: any = {};
            if (this.isEdited()) {
                result = await getConfirmationBox({
                    message: alertMessage.CONFIRM_CONTINUE,
                    showCloseButton: true,
                    confirmButtonColor: '',
                    title: alertMessage.UNDO_CURRENT_CHANGE
                });
            } else {
                this.setState({
                    questionTypeModal: true,
                    addQuestion: { position, parent }
                });
            }
            if (result.value) {
                this.setState({
                    questionTypeModal: true,
                    addQuestion: { position, parent }
                });
            }
        } catch (e) {}
    };
    onSelectedQuestionTypeDone = (questionType: QTYPES) => {
        const { parent, position } = this.state.addQuestion;
        this.props.setAddNewQuestion(questionType, position, parent);

        this.setState({
            questionTypeModal: false,
            addQuestion: {}
        });
    };

    onSelectedQuestionTypeClose = () => {
        this.setState({
            questionTypeModal: false,
            isAudience: false,
            addQuestion: {}
        });

        clearLocationState(this.props.history, this.props.location);
    };

    onMove = (dragIndex: number, hoverIndex: number, questionId: number) => {
        this.props.sortQuestions(
            this.props.projectId,
            dragIndex,
            hoverIndex,
            questionId
        );
    };

    dragStop = () => {
        this.props.saveUpdatedSortedQuestion(this.props.projectId);
    };

    filterQuestions = () => {
        return this.props.questionSummaries.filter(
            (questionSummary: QuestionSummary) => {
                // to support label as empty string
                if (questionSummary.hasOwnProperty('label')) {
                    return (
                        questionSummary.label.indexOf(
                            this.state.questionSearch
                        ) > -1
                    );
                } else {
                    return (
                        questionSummary?.['title'].indexOf(
                            this.state.questionSearch
                        ) > -1
                    );
                }
            }
        );
    };

    onDuplicateQuestion = async (question) => {
        this.setState({
            isDuplicateQuestion: true,
            duplicatedQuestion: question
        });
    };

    closeDuplicateProjectModal = () => {
        this.setState({
            isDuplicateQuestion: false
        });
    };

    createDuplicateQuestion = async (
        duplicatedQuestion,
        values: any,
        { setSubmitting }
    ) => {
        const { questionTitle } = values;
        const question = {
            id: duplicatedQuestion.id,
            questionTitle
        };

        try {
            await this.props.duplicateQuestion(question);
            this.props.toastNotification.success(
                parseAlertMessage(
                    alertMessage.QUESTION_DUPLICATE_QUESTION,
                    duplicatedQuestion.title || duplicatedQuestion.label
                )
            );
            this.setState({ isDuplicateQuestion: false });
            setSubmitting(false);
        } catch (e) {
            this.props.toastNotification.error(
                alertMessage.GENERIC_ERROR_MESSAGE
            );
            setSubmitting(false);
        }
    };

    render() {
        const questionSummaries = this.filterQuestions();
        return (
            <div className="project-add--question">
                <div className="row">
                    {this.state.isDuplicateQuestion &&
                    Object.keys(this.state.duplicatedQuestion).length != 0 ? (
                        <DuplicateQuestionModal
                            duplicatedQuestion={this.state.duplicatedQuestion}
                            handleSubmit={this.createDuplicateQuestion}
                            onClose={this.closeDuplicateProjectModal}
                        />
                    ) : null}
                    <div className="col-md-3 left-wrapper">
                        <QuestionListView
                            isLoading={this.state.isLoading}
                            search={this.state.questionSearch}
                            questionSummaries={questionSummaries}
                            actions={{
                                edit: this.onQuestionEdit,
                                delete: this.onQuestionDelete,
                                logic: this.onQuestionLogic,
                                preview: this.onQuestionPreview,
                                duplicate: this.onDuplicateQuestion
                            }}
                            onAdd={this.onNewQuestionAdd}
                            moveCard={this.onMove}
                            dragStop={this.dragStop}
                            onSearchChange={this.onQuestionSearch}
                            selectedQuestion={this.props.currentQuestion}
                            onSelectQuestion={this.onQuestionSelect}
                            currentProject={this.props.currentProject}
                            editEnabled={this.props.editEnabled}
                            projectHealth={this.props.projectHealth}
                            editMode={this.props.editMode}
                            initialSelectedQuestionID={
                                this.state.initialSelectedQuestionID
                            }
                            showError={this.props.showError}
                        />
                    </div>
                    <div className="col-md-9 right-wrapper">
                        <QuestionBuilder
                            key={`${this.props.currentQuestionId}-
                                ${this.props.formRenderCount}`}
                            isLoading={this.state.isQuestionLoading}
                            builderState={this.props.builderState}
                            isPreviewReady={this.state.isPreviewReady}
                            isEdited={this.isEdited()}
                            onQuestionPreview={this.onQuestionPreview}
                            onQuestionEdit={this.onQuestionEdit}
                            onQuestionLogic={this.onQuestionLogic}
                        />
                    </div>
                </div>
                {(this.state.questionTypeModal && (
                    <QuestionTypesModal
                        onCancel={this.onSelectedQuestionTypeClose}
                        onDone={this.onSelectedQuestionTypeDone}
                        isOpen={this.state.questionTypeModal}
                        isAudience={this.state.isAudience}
                        viewMode={this.props.viewMode}
                    />
                )) ||
                    null}
            </div>
        );
    }
}

const mapStateToProps = ({
    router,
    questionReducer,
    projectReducer,
    utilsReducer
}: IApplicationState) => {
    const projectId = splitPathname(router.location.pathname);

    return {
        projectId,
        questionSummaries: questionReducer.questionSummaries[projectId] || [],
        currentQuestion: questionReducer.currentQuestion,
        currentQuestionId: questionReducer.currentQuestionId,
        builderState: questionReducer.builderState,
        isAdd: questionReducer.isNewQuestion,
        currentProject: projectReducer.currentProject,
        isEdited: utilsReducer.isEdited,
        editMode: projectReducer.editMode,
        editedType: utilsReducer.editedType,
        editEnabled: projectReducer.editEnabled['question'],
        projectHealth: sortHealthProblemByQuestionID(
            projectReducer.projectHealth
        ),
        viewMode: utilsReducer.viewMode,
        currentLockUser: projectReducer.currentLockUser,
        formRenderCount: questionReducer.formRenderCount,
        showError: questionReducer.showError
    };
};

const mapDispatchToProps = (dispatch) => ({
    editGreeting: (type: QTYPES) => dispatch(editGreeting(type)),
    setLogicQuestion: ({ question, questionId, builderState }) =>
        dispatch(setLogicQuestion({ question, questionId, builderState })),
    setAddNewQuestion: (
        questionType: string,
        position: number,
        parent: number
    ) => dispatch(setAddNewQuestion({ questionType, position, parent })),
    editQuestion: (questionType: string, questionId: number) =>
        dispatch(editQuestion({ questionType, questionId })),
    previewQuestion: (projectId: number, questionId: number) =>
        dispatch(previewQuestion(projectId, questionId)),
    deleteQuestion: (question: any, isParent: boolean) =>
        dispatch(deleteQuestion(question, isParent)),
    duplicateQuestion: (question: any) => dispatch(duplicateQuestion(question)),
    clearQuestionStatus: ({ currentQuestion, builderState }) =>
        dispatch(clearQuestionStatus({ currentQuestion, builderState })),
    getSortedQuestionByProjectId: (projectId: number) =>
        dispatch(getSortedQuestionByProjectId(projectId)),
    getQuestionByQuestionId: (questionId: number, options?: any) =>
        dispatch(getQuestionByQuestionId(questionId, options)),
    sortQuestions: (
        projectId: number,
        dragIndex: number,
        hoverIndex: number,
        questionId: number,
        collection: number
    ) => dispatch(sortQuestions(projectId, dragIndex, hoverIndex, questionId)),
    saveUpdatedSortedQuestion: (projectId: number) =>
        dispatch(saveUpdatedSortedQuestion(projectId)),
    getTrainedModelSummaries: () => dispatch(getTrainedModelSummaries())
});

export default DragDropContext(HTML5Backend)(
    ProjectHOC(
        NotifyHOC(connect(mapStateToProps, mapDispatchToProps)(Questions))
    )
);
